import { cssMap } from '@atlaskit/css';

import DropdownItemGroup from '@atlaskit/dropdown-menu/dropdown-menu-item-group';

import { Box } from '@atlaskit/primitives/compiled/box';

import { Text } from '@atlaskit/primitives/compiled/text';

import { token } from '@atlaskit/tokens';

import { useFireCrossFlowAnalyticsEvents } from '@atlassian/surface-analytics/cross-flow/hooks';

import { Targets } from '@atlassiansox/cross-flow-support/cross-flow-base-types';

import { useFirePostOfficeAnalyticsEvents } from '@post-office/analytics';

import { createPlacementComponent } from '@post-office/placement-common/utils/create-placement-component';

import type { PlacementComponent } from '@post-office/placement-contexts/create-placement-component/types';

import type { MessageProps } from '@post-office/placement-common/utils/message-renderer';

import { useInitialData } from '@post-office/placement-contexts/hooks';

import { useCallback, useEffect, useState } from 'react';

import { FormattedMessage } from 'react-intl';

import { MessageRenderer, type renderer } from '../__generated__/client/render';

import { PLACEMENT_ID } from '../__generated__/common';

import { withJiraIssueviewQuickactionsIntlProvider } from '../__generated__/intl-provider';

import { type JiraIssueviewQuickactionsInitialData } from '../api/types';

import { type Placement as JiraIssueviewQuickactionsPlacement } from '../contracts/Placement';

export type Message = MessageProps<typeof renderer>;

const styles = cssMap({
	header: {
		paddingBlock: token('space.100'),
		paddingInline: token('space.200'),
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		minWidth: '250px',
	},
});

export const JiraIssueviewQuickactionsPlacementComponent: PlacementComponent<
	Record<string, unknown>
> = withJiraIssueviewQuickactionsIntlProvider(
	createPlacementComponent<JiraIssueviewQuickactionsPlacement>(
		PLACEMENT_ID,
		({
			initialData,
			onDismissed,
			onAdsShown,
			onClick,
			onCrossflowApiOpenComplete,
			issueId,
			issueKey,
		}) => {
			const { data, isLoading } = useInitialData<JiraIssueviewQuickactionsInitialData>(initialData);
			const [messages, setMessages] = useState<Message[]>([]);
			const { fireAnalyticsEvent } = useFirePostOfficeAnalyticsEvents();
			const { fireViewedUIEvent } = useFireCrossFlowAnalyticsEvents();

			const handleOnDismissed = useCallback(
				(messageTemplateId: string) => {
					setMessages(
						// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
						messages?.filter((message) => message.messageTemplateId !== messageTemplateId),
					);
					onDismissed?.(messageTemplateId);
				},
				[messages, onDismissed],
			);

			useEffect(() => {
				if (!isLoading) {
					const newMessages = data?.messages as Message[];
					setMessages(newMessages);
					fireAnalyticsEvent({
						eventType: 'screen',
						name: 'quickactions-xflow',
						attributes: {
							...newMessages.reduce<Record<string, boolean>>((acc, val) => {
								// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
								acc[val.messageTemplateId] = true;
								return acc;
							}, {}),
						},
					});
					// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
					const newMessageTemplateIds = newMessages.map((message) => message.messageTemplateId);
					// eslint-disable-next-line @typescript-eslint/no-unsafe-argument
					onAdsShown?.(newMessageTemplateIds);

					// fire relavant cross flow viewed events
					if (newMessageTemplateIds.includes('jira-xflow-confluence')) {
						fireViewedUIEvent(
							{
								action: 'viewed',
								actionSubject: 'listItem',
								actionSubjectId: 'quickAddConfluenceListItem',
							},
							{
								recommendedProductIds: [Targets.CONFLUENCE],
								source: 'quickAddConfluenceListItem',
							},
						);
					}

					if (newMessageTemplateIds.includes('jira-xflow-jpd')) {
						fireViewedUIEvent(
							{
								action: 'viewed',
								actionSubject: 'listItem',
								actionSubjectId: 'quickAddIdeaListItem',
							},
							{
								recommendedProductIds: [Targets.JIRA_PRODUCT_DISCOVERY],
								source: 'quickAddIdeaListItem',
							},
						);
					}
				}
			}, [data, fireAnalyticsEvent, isLoading, onAdsShown]);

			if (isLoading) {
				return null;
			}

			return (
				messages &&
				messages.length > 0 && (
					<DropdownItemGroup hasSeparator>
						<Box testId="jira-issueview-quickactions-message" xcss={styles.header}>
							<Text weight="bold" size="small" color="color.text.subtle">
								<FormattedMessage
									id="jira-issueview-quickactions.src.ui.index.recommended-for-you"
									description="Quick actions section header for showing cross flow actions"
									defaultMessage="Recommended for you"
								/>
							</Text>
						</Box>
						{messages.map((message) => (
							<MessageRenderer
								{...message}
								// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
								id={message.messageInstanceId}
								onDismissed={handleOnDismissed}
								onClick={onClick}
								onCrossflowApiOpenComplete={onCrossflowApiOpenComplete}
								issueId={issueId}
								issueKey={issueKey}
							/>
						))}
					</DropdownItemGroup>
				)
			);
		},
		{
			choreographer: {
				isDefaultEnabled: false,
			},
		},
	),
);

export default JiraIssueviewQuickactionsPlacementComponent;
